// Played latency: when translated audio for a customer turn actually starts
// playing in the agent's browser.
//
// The ML API reports when it sent the turn's first audio frame (epoch ms, its
// clock). Here we listen to the remote audio, note each sound onset after
// silence, keep our clock synced to the server's by round trips over the
// CallChannel, and when a `playback_probe` arrives for a turn, match it to the
// first unclaimed onset after its sent time and report it back.
//
// Measurement only: nothing here changes what the agent hears or sees.

export const DEFAULTS = {
  threshold: 0.02, // RMS (0..1) that counts as sound
  minSilenceMs: 250, // silence needed before a sound counts as a new onset
  keepMs: 60_000, // onsets kept for matching
  toleranceMs: 150, // an onset may appear this much before the sent time (clock sync error)
  maxDelayMs: 2_000, // later than this after the sent time is another sound
};

export class OnsetDetector {
  constructor({ threshold = DEFAULTS.threshold, minSilenceMs = DEFAULTS.minSilenceMs, keepMs = DEFAULTS.keepMs } = {}) {
    this.threshold = threshold;
    this.minSilenceMs = minSilenceMs;
    this.keepMs = keepMs;
    this.onsets = [];
    this.silentSince = null; // when the current silence began; null while sounding
  }

  // Feed one level reading. Returns the onset if this reading starts one.
  push(level, atMs, visibility = "visible") {
    let onset = null;
    if (level >= this.threshold) {
      if (this.silentSince !== null && atMs - this.silentSince >= this.minSilenceMs) {
        onset = { atMs, level, gapMs: atMs - this.silentSince, visibility };
        this.onsets.push(onset);
      }
      this.silentSince = null;
    } else if (this.silentSince === null) {
      this.silentSince = atMs;
    }
    this.onsets = this.onsets.filter((o) => atMs - o.atMs <= this.keepMs);
    return onset;
  }
}

// samples: [{ clientSentMs, clientReceivedMs, serverEpochMs }]. The round trip
// with the least delay has the least uncertainty about when the server answered.
export function bestClockOffset(samples) {
  if (!samples.length) return null;
  const best = samples.reduce((a, b) =>
    b.clientReceivedMs - b.clientSentMs < a.clientReceivedMs - a.clientSentMs ? b : a);
  const rttMs = best.clientReceivedMs - best.clientSentMs;
  return { offsetMs: best.serverEpochMs - (best.clientSentMs + best.clientReceivedMs) / 2, rttMs };
}

// First unclaimed onset whose server-clock time falls in [sent - tolerance, sent + maxDelay].
export function matchOnset(onsets, sentServerMs, offsetMs,
  { toleranceMs = DEFAULTS.toleranceMs, maxDelayMs = DEFAULTS.maxDelayMs } = {}) {
  for (const onset of onsets) {
    if (onset.claimed) continue;
    const playedServerMs = onset.atMs + offsetMs;
    const delay = playedServerMs - sentServerMs;
    if (delay >= -toleranceMs && delay <= maxDelayMs) return { playedServerMs, onset };
  }
  return null;
}

// Why a turn could not be matched, so a miss is a diagnosis rather than a gap.
export function describeMiss(onsets, sentServerMs, { clock, audioAttached, audioState },
  { toleranceMs = DEFAULTS.toleranceMs, maxDelayMs = DEFAULTS.maxDelayMs, recentMs = 10_000 } = {}) {
  const miss = {
    result: "no_match",
    clock_offset_ms: clock ? clock.offsetMs : null,
    rtt_ms: clock ? clock.rttMs : null,
    audio_attached: audioAttached,
    audio_state: audioState,
    onsets_last_10s: 0,
    nearest_onset_delay_ms: null,
  };
  if (!clock) return { ...miss, reason: "no_clock" };
  if (!audioAttached) return { ...miss, reason: "no_audio" };
  if (audioState !== "running") return { ...miss, reason: "audio_suspended" };
  const delays = onsets.map((o) => ({ delay: o.atMs + clock.offsetMs - sentServerMs, claimed: o.claimed }))
    .filter((d) => Math.abs(d.delay) <= recentMs);
  miss.onsets_last_10s = delays.length;
  if (!delays.length) return { ...miss, reason: "no_onsets" };
  const nearest = delays.reduce((a, b) => (Math.abs(b.delay) < Math.abs(a.delay) ? b : a));
  miss.nearest_onset_delay_ms = Math.round(nearest.delay);
  const inWindow = delays.some((d) => d.delay >= -toleranceMs && d.delay <= maxDelayMs);
  return { ...miss, reason: inWindow ? "claimed" : "no_onset_in_window" };
}

// Browser wiring. `getStream` returns the remote MediaStream once the call has one.
export class PlayedLatencyObserver {
  constructor({ subscription, getStream, now = () => performance.timeOrigin + performance.now() }) {
    this.subscription = subscription;
    this.getStream = getStream;
    this.now = now;
    this.detector = new OnsetDetector();
    this.clockSamples = [];
    this.timers = [];
  }

  start() {
    this.syncClock();
    this.timers.push(setInterval(() => this.syncClock(), 60_000));
    this.timers.push(setInterval(() => this.attachAudio(), 1_000));
  }

  stop() {
    this.timers.forEach((timer) => clearInterval(timer));
    this.timers = [];
    if (this.audioContext) this.audioContext.close().catch(() => {});
    this.audioContext = null;
  }

  // A short burst of round trips; the best one sets the offset.
  syncClock() {
    for (let i = 0; i < 5; i += 1) {
      setTimeout(() => this.subscription.perform("clock_sync", { client_sent_ms: this.now() }), i * 200);
    }
  }

  handleClockSync(data) {
    this.clockSamples.push({
      clientSentMs: Number(data.client_sent_ms),
      clientReceivedMs: this.now(),
      serverEpochMs: Number(data.server_epoch_ms),
    });
    this.clockSamples = this.clockSamples.slice(-20);
  }

  attachAudio() {
    if (this.audioContext) return;
    const stream = this.getStream();
    if (!stream || !stream.getAudioTracks || stream.getAudioTracks().length === 0) return;
    try {
      this.audioContext = new AudioContext();
      this.audioContext.resume().catch(() => {});
      const analyser = this.audioContext.createAnalyser();
      analyser.fftSize = 512;
      this.audioContext.createMediaStreamSource(stream).connect(analyser);
      const buffer = new Float32Array(analyser.fftSize);
      this.timers.push(setInterval(() => {
        analyser.getFloatTimeDomainData(buffer);
        let sum = 0;
        for (const sample of buffer) sum += sample * sample;
        this.detector.push(Math.sqrt(sum / buffer.length), this.now(), document.visibilityState);
      }, 10));
    } catch (error) {
      console.warn("Played latency: could not observe remote audio", error);
      this.audioContext = null;
    }
  }

  // The turn's audio may still be on its way when the probe arrives: retry briefly.
  handleProbe(data, attempt = 0) {
    const clock = bestClockOffset(this.clockSamples);
    const sent = Number(data.first_frame_sent_epoch_ms);
    const match = clock && Number.isFinite(sent) ? matchOnset(this.detector.onsets, sent, clock.offsetMs) : null;
    if (!match) {
      if (attempt < 10) {
        setTimeout(() => this.handleProbe(data, attempt + 1), 300);
      } else {
        this.subscription.perform("playback_unmatched", {
          turn_id: data.turn_id,
          observation: {
            detector: "rms_onset_v1",
            visibility: document.visibilityState,
            ...describeMiss(this.detector.onsets, sent, {
              clock, audioAttached: Boolean(this.audioContext), audioState: this.audioContext?.state ?? null,
            }),
          },
        });
      }
      return;
    }
    match.onset.claimed = true;
    this.subscription.perform("playback_observed", {
      turn_id: data.turn_id,
      played_epoch_ms: match.playedServerMs,
      observation: {
        detector: "rms_onset_v1",
        clock_offset_ms: clock.offsetMs,
        rtt_ms: clock.rttMs,
        onset_level: match.onset.level,
        gap_ms: match.onset.gapMs,
        visibility: match.onset.visibility,
        output_latency_ms: this.audioContext ? (this.audioContext.outputLatency || 0) * 1000 : null,
      },
    });
  }
}
